import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import * as repo from '../data/votingRepository';
import { votingErrorMessage } from '../utils/votingErrors';

export const votingKeys = {
  elections: ['voting', 'elections'] as const,
  state: (id: string) => ['voting', 'state', id] as const,
  voters: (id: string) => ['voting', 'voters', id] as const,
  raceVotes: (raceId: string) => ['voting', 'race-votes', raceId] as const,
  events: ['voting', 'events'] as const,
  sessions: (eventId: string) => ['voting', 'sessions', eventId] as const,
};

export function useElections() {
  return useQuery({ queryKey: votingKeys.elections, queryFn: repo.listElections });
}

/**
 * The whole election in one call. Polls every 3 s: it also closes timed rounds that expired,
 * and keeps open screens in sync (new rounds, turnout).
 */
export function useElectionState(electionId: string | undefined) {
  return useQuery({
    queryKey: votingKeys.state(electionId ?? ''),
    queryFn: async () => ({ state: await repo.getElectionState(electionId as string), fetchedAt: Date.now() }),
    enabled: !!electionId,
    refetchInterval: 3000,
  });
}

export function useEventsForPicker() {
  return useQuery({ queryKey: votingKeys.events, queryFn: repo.listEventsForPicker, staleTime: 60_000 });
}

export function useSessionsForPicker(eventId: string | null | undefined) {
  return useQuery({
    queryKey: votingKeys.sessions(eventId ?? ''),
    queryFn: () => repo.listSessionsForPicker(eventId as string),
    enabled: !!eventId,
  });
}

export function useManualVoters(electionId: string, enabled: boolean) {
  return useQuery({
    queryKey: votingKeys.voters(electionId),
    queryFn: () => repo.listManualVoters(electionId),
    enabled,
  });
}

export function useRaceVotes(raceId: string | undefined) {
  return useQuery({
    queryKey: votingKeys.raceVotes(raceId ?? ''),
    queryFn: () => repo.listRaceVotes(raceId as string),
    enabled: !!raceId,
  });
}

/** Mutation that toasts errors in Spanish and refreshes the election state */
export function useVotingMutation<TArgs>(
  electionId: string | undefined,
  fn: (args: TArgs) => Promise<unknown>,
  options?: { success?: string; extraKeys?: readonly (readonly unknown[])[] },
) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (options?.success) toast({ title: options.success });
      if (electionId) void qc.invalidateQueries({ queryKey: votingKeys.state(electionId) });
      void qc.invalidateQueries({ queryKey: votingKeys.elections });
      options?.extraKeys?.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
    },
    onError: (err) => toast({ title: votingErrorMessage(err), variant: 'destructive' }),
  });
}
